import { AlertCircle, Check, ChevronDown, Film, LoaderCircle, MoreHorizontal, User, X } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import type { Character, Creative, DeliveryStatus, HypothesisStatus, Moderation, Verdict, WorkspaceSettings } from "@lovmia/shared";
import { deliveryStatusLabels, hypothesisStatusLabels, moderationStatusLabels, moderationVerdict, verdictLabels } from "@lovmia/shared";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return <div className="page-header">
    <div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h1>{title}</h1>{description && <p>{description}</p>}</div>
    {actions && <div className="page-actions">{actions}</div>}
  </div>;
}

export function Button({ children, variant = "secondary", loading, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "primary" | "secondary" | "ghost" | "danger"; loading?: boolean }) {
  return <button {...props} disabled={props.disabled || loading} className={`button button--${variant} ${props.className ?? ""}`}>
    {loading ? <LoaderCircle className="spin" size={15} /> : children}
  </button>;
}

export function StatusPill({ status }: { status: HypothesisStatus }) {
  return <span className={`status-pill status-pill--${status}`}>{hypothesisStatusLabels[status]}</span>;
}

export function VerdictPill({ verdict }: { verdict: Verdict | null }) {
  if (!verdict) return <span className="status-pill status-pill--quiet">Без вердикта</span>;
  return <span className={`verdict-pill verdict-pill--${verdict}`}>{verdictLabels[verdict]}</span>;
}

export function Metric({ label, value, tone }: { label: string; value: ReactNode; tone?: "good" | "bad" | "warn" }) {
  return <div className={`metric ${tone ? `metric--${tone}` : ""}`}><span>{label}</span><strong>{value}</strong></div>;
}

export function Tag({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "pain" | "desire" | "accent" }) {
  return <span className={`tag tag--${tone}`}>{children}</span>;
}

export function EmptyState({ icon = "film", title, text, action }: { icon?: "film" | "check" | "alert"; title: string; text: string; action?: ReactNode }) {
  const Icon = icon === "check" ? Check : icon === "alert" ? AlertCircle : Film;
  return <div className="empty-state"><div><Icon size={22} /></div><strong>{title}</strong><p>{text}</p>{action}</div>;
}

export function Modal({ children, onClose, wide = false }: { children: ReactNode; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = previousOverflow; };
  }, [onClose]);
  return createPortal(<motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <motion.div role="dialog" aria-modal="true" className={`modal-panel ${wide ? "modal-panel--wide" : ""}`} initial={{ opacity: 0, y: 24, scale: .985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: .99 }} transition={{ duration: .24 }}>
      <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть"><X size={18} /></button>{children}
    </motion.div>
  </motion.div>, document.body);
}

/**
 * Лайтбокс: картинка во весь экран поверх всего остального.
 *
 * Отдельно от `Modal`, хотя оба портала с затемнением: у модалки панель со
 * своей шириной, отступами и скроллом, а здесь главное — сама картинка, и любой
 * контейнер вокруг неё только отъедает место. Открывается в том числе поверх
 * открытой модалки крео, поэтому лежит на своём слое.
 */
export function Lightbox({ src, alt, title, caption, onClose }: {
  src: string;
  alt: string;
  title?: string;
  caption?: string | null;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = previousOverflow; };
  }, [onClose]);
  return createPortal(<motion.div
    className="lightbox-backdrop"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
  >
    <button type="button" className="lightbox-close" onClick={onClose} aria-label="Закрыть"><X size={20} /></button>
    <motion.figure
      className="lightbox-figure"
      initial={{ opacity: 0, scale: .97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: .98 }}
      transition={{ duration: .2 }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <img src={src} alt={alt} />
      {(title || caption) && <figcaption>{title && <strong>{title}</strong>}{caption && <p>{caption}</p>}</figcaption>}
    </motion.figure>
  </motion.div>, document.body);
}

/**
 * Лицо персонажа. Пока листа нет, показываем инициал, а не пустой квадрат:
 * персонажа заводят словами раньше, чем генерация отдаёт картинку.
 */
export function CharacterAvatar({ character, size = "md" }: { character: Character; size?: "sm" | "md" | "lg" }) {
  if (character.image) {
    return <img className={`character-avatar character-avatar--${size}`} src={character.image.url} alt={character.name} loading="lazy" />;
  }
  return <span className={`character-avatar character-avatar--${size} character-avatar--empty`} aria-hidden="true">
    {character.name.trim().charAt(0).toUpperCase() || <User size={14} />}
  </span>;
}

export interface MenuAction {
  label: string;
  icon?: ReactNode;
  tone?: "default" | "danger";
  onSelect: () => void;
}

/**
 * Меню действий у элемента списка. Список рисуется порталом с фиксированными
 * координатами: колонки канбана и таблица реестра скроллятся с `overflow`, и
 * выпадашка внутри потока обрезалась бы контейнером.
 *
 * Триггер намеренно не вкладывается в карточку — карточка сама кнопка, и
 * кнопка внутри кнопки не работает ни в разметке, ни для клавиатуры.
 */
export function Menu({ actions, label = "Действия", className = "" }: { actions: MenuAction[]; label?: string; className?: string }) {
  const trigger = useRef<HTMLButtonElement>(null);
  const [origin, setOrigin] = useState<{ top: number; right: number } | null>(null);
  const close = useCallback(() => setOrigin(null), []);

  useLayoutEffect(() => {
    if (!origin) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") close(); };
    // Скролл и ресайз уводят меню от своего элемента — проще закрыть, чем гнать
    // за ним координаты.
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [origin, close]);

  const open = () => {
    const rect = trigger.current?.getBoundingClientRect();
    if (rect) setOrigin({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
  };

  if (!actions.length) return null;
  return <>
    <button
      ref={trigger}
      type="button"
      className={`menu-trigger ${className}`}
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={origin != null}
      onClick={(event) => { event.stopPropagation(); origin ? close() : open(); }}
    ><MoreHorizontal size={15} /></button>
    {origin && createPortal(<div className="menu-scrim" onMouseDown={close} onContextMenu={(event) => { event.preventDefault(); close(); }}>
      <div
        role="menu"
        className="menu-panel"
        style={{ top: origin.top, right: origin.right }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {actions.map((action) => <button
          key={action.label}
          role="menuitem"
          type="button"
          className={action.tone === "danger" ? "menu-item menu-item--danger" : "menu-item"}
          onClick={() => { close(); action.onSelect(); }}
        >{action.icon}{action.label}</button>)}
      </div>
    </div>, document.body)}
  </>;
}

/** Подтверждение необратимого действия: что именно случится и кнопка с тоном. */
export function ConfirmDialog({ title, description, confirmLabel, tone = "danger", loading, onConfirm, onClose }: {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  tone?: "danger" | "primary";
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return <Modal onClose={onClose}><div className="dialog-form">
    <h2>{title}</h2>
    <div className="confirm-body">{description}</div>
    <div className="dialog-actions">
      <Button type="button" variant="ghost" onClick={onClose}>Отмена</Button>
      <Button type="button" variant={tone} loading={loading} onClick={onConfirm}>{confirmLabel}</Button>
    </div>
  </div></Modal>;
}

/**
 * Слова кабинета, которых доска больше не говорит (GEN-2250). Пауза и
 * удаление — рабочие движения UA с бюджетом, а не событие крео. Реджект
 * заменён счётчиком модерации: одно отклонённое объявление из десяти — не то
 * же самое, что «отклонено», а худший статус группы говорил именно так.
 */
const silentDeliveryStatuses: ReadonlySet<DeliveryStatus> = new Set(["paused", "deleted", "rejected"]);

/**
 * Слово кабинета об объявлении. Штатную открутку не подписываем — её видно по
 * спенду; чип нужен там, где крео стоит на нуле не потому, что «ещё не
 * набрало», а потому что объявление ещё на модерации.
 */
export function DeliveryChip({ creative, always = false }: { creative: Pick<Creative, "deliveryStatus" | "statusDetail">; always?: boolean }) {
  const status = creative.deliveryStatus;
  if (!status || silentDeliveryStatuses.has(status)) return null;
  if (!always && (status === "active" || status === "unknown")) return null;
  return <span className={`delivery-chip delivery-chip--${status}`} title={creative.statusDetail ?? "Статус объявления в кабинете"}>{deliveryStatusLabels[status]}</span>;
}

/**
 * Модерация по всем кабинетам: «реджекты / запуски», цвет — светофор, рядом
 * статус, если он сработал. Пока фид ничего не присылал, чипа нет вовсе:
 * «0/0» читалось бы как измеренный факт.
 */
export function ModerationChip({ moderation, thresholds, statusOnly = false }: {
  moderation: Moderation | null;
  thresholds: WorkspaceSettings["moderation"];
  statusOnly?: boolean;
}) {
  if (!moderation) return null;
  const verdict = moderationVerdict(moderation, thresholds);
  const percent = moderation.rejected > 0 ? Math.round(verdict.rate * 100) : null;
  const title = [
    `Реджекты / запуски по всем кабинетам: ${moderation.rejected} из ${moderation.launched}`,
    moderation.rejectedAccounts.length ? `Кабинеты с реджектами: ${moderation.rejectedAccounts.join(", ")}` : null,
    moderation.quarantine ? `Creative Quarantine: ${moderation.quarantine === "quarantined" ? "помечено вручную" : "suspect, 3+ реджекта"}` : null
  ].filter(Boolean).join("\n");
  return <>
    {!statusOnly && <span className={`mod-chip mod-chip--${verdict.tone}`} title={title}>
      <i aria-hidden="true" />{moderation.rejected}/{moderation.launched}{percent != null && <small>{percent}%</small>}
    </span>}
    {verdict.status && <span className={`mod-status mod-status--${verdict.status}`} title={title}>{moderationStatusLabels[verdict.status]}</span>}
  </>;
}

export function Select({ children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <label className="select-wrap"><select {...props}>{children}</select><ChevronDown size={14} /></label>;
}

/**
 * Превью крео. Пока файла нет, показываем честную заглушку, а не «плеер»,
 * который ничего не проигрывает: в крупном варианте прямо пишем, что файла нет.
 *
 * `controls` включается там, где ролик смотрят (карточка крео, Диагностика), и
 * НЕ включается в превью на канбане: там `<video>` лежит внутри карточки-кнопки,
 * и кнопки плеера внутри `<button>` — и невалидная разметка, и перехваченный
 * клик по самой карточке.
 */
export function CreativeVisual({ creative, compact = false, controls = false }: {
  creative: Creative; compact?: boolean; controls?: boolean;
}) {
  const [posterFailed, setPosterFailed] = useState(false);
  const label = creative.format === "slideshow" ? "SLIDES" : creative.format === "static" ? "STATIC" : "VIDEO";
  // Превью — серверный JPEG-постер (/poster: генерится раз, кэшируется вечно),
  // а не <video>: тамб не должен тянуть ролик ради одного кадра. Плеер получает
  // постер атрибутом — кадр виден мгновенно, до загрузки видео; #t=0.1 остаётся
  // страховкой отрисовки для Safari. Постер может не существовать (старый
  // деплой, нет ffmpeg на сервере) — тогда тамб честно падает обратно на
  // <video>-превью, а не показывает битую картинку.
  if (creative.file) {
    // Фотокрео (format static/slideshow) — картинка и есть само крео: ни плеера,
    // ни постера ей не нужно, файл показывается как есть в обоих размерах.
    if (creative.file.mimeType.startsWith("image/")) {
      return <div className={`creative-visual ${compact ? "creative-visual--compact" : ""}`}>
        <img src={creative.file.url} alt={`Превью ${creative.id}`} loading="lazy" />
      </div>;
    }
    const poster = `/api/files/${creative.file.id}/poster`;
    if (controls) return <div className={`creative-visual ${compact ? "creative-visual--compact" : ""}`}>
      <video
        src={`${creative.file.url}#t=0.1`}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        aria-label={`Превью ${creative.id}`}
      />
    </div>;
    return <div className={`creative-visual ${compact ? "creative-visual--compact" : ""}`}>
      {posterFailed
        ? <video src={`${creative.file.url}#t=0.1`} muted playsInline preload="metadata" aria-label={`Превью ${creative.id}`} />
        : <img src={poster} alt={`Превью ${creative.id}`} loading="lazy" onError={() => setPosterFailed(true)} />}
    </div>;
  }
  if (!compact) return <div className="creative-visual creative-visual--empty" role="img" aria-label={`${creative.id}: файл ещё не загружен`}>
    <Film size={22} /><strong>Файл ещё не загружен</strong><small>{label} · {creative.id}</small>
  </div>;
  return <div className="creative-visual creative-visual--placeholder creative-visual--compact" role="img" aria-label={`${creative.id}: файл ещё не загружен`}>
    <div className="visual-person"><i /></div><em>{label}</em>
  </div>;
}
